import { useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import React, { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { UnsavedChangesHandlersContext } from '../../../../utils/use-unsaved-changes-handlers';
import { CollapseListContent } from './collapse-list-content';
import { createCollapseListAtoms } from './create-collapse-list-atoms';
import { calculateAndScrollIntoView, ScrollIntoViewContext } from './scroll-into-view';

// Main CollapseList Props

// React context to expose this instance’s atoms to its items
export const CollapseContext = /*#__PURE__*/createContext(null);

// Main CollapseList Component
export const CollapseList = _ref => {
  let {
    children,
    singleOpenMode = true,
    dataHook,
    contentGap = 'SP4',
    showDividers = true,
    openItems,
    initialOpenItems,
    setOpenItems,
    autoScroll,
    scrollContainerRef,
    dividerPadding = '0 SP4',
    unsavedChangesHandlers
  } = _ref;
  const atomsRef = useRef(null);
  if (!atomsRef.current) {
    atomsRef.current = createCollapseListAtoms(initialOpenItems);
  }
  const atoms = atomsRef.current;
  const parentScrollIntoView = React.useContext(ScrollIntoViewContext);
  const containerRef = scrollContainerRef ?? (parentScrollIntoView == null ? void 0 : parentScrollIntoView.containerRef);
  const scrollToElement = useCallback((headerEl, contentEl) => calculateAndScrollIntoView(containerRef, headerEl, contentEl), [containerRef]);
  const scrollIntoViewContextValue = useMemo(() => {
    // Determine enabled value based on autoScroll prop and parent context
    let enabled;
    if (autoScroll === true) {
      enabled = true;
    } else if (autoScroll === false) {
      enabled = false;
    } else {
      // autoScroll is undefined, inherit from parent context
      enabled = (parentScrollIntoView == null ? void 0 : parentScrollIntoView.enabled) ?? false;
    }
    return {
      containerRef,
      enabled,
      scrollToElement
    };
  }, [containerRef, autoScroll, scrollToElement, parentScrollIntoView == null ? void 0 : parentScrollIntoView.enabled]);
  useHydrateAtoms([[atoms.singleOpenModeAtom, singleOpenMode]]);
  const setCurrentOpenItemsAtom = useSetAtom(atoms.currentOpenItemsAtom);
  const setControlledCallbacksAtom = useSetAtom(atoms.controlledCallbacksAtom);
  useEffect(() => {
    if (openItems !== undefined && setOpenItems !== undefined) {
      setCurrentOpenItemsAtom(openItems);
      setControlledCallbacksAtom({
        setOpenItems
      });
    }
  }, [openItems, setOpenItems, setCurrentOpenItemsAtom, setControlledCallbacksAtom]);
  const content = /*#__PURE__*/React.createElement(ScrollIntoViewContext.Provider, {
    value: scrollIntoViewContextValue
  }, /*#__PURE__*/React.createElement(CollapseContext.Provider, {
    value: atoms
  }, /*#__PURE__*/React.createElement(CollapseListContent, {
    contentGap: contentGap,
    dataHook: dataHook,
    dividerPadding: dividerPadding,
    showDividers: showDividers
  }, children)));
  return unsavedChangesHandlers ? /*#__PURE__*/React.createElement(UnsavedChangesHandlersContext.Provider, {
    value: unsavedChangesHandlers
  }, content) : content;
};
//# sourceMappingURL=collapse-list.js.map